import * as WretchLib from 'wretch';
import {ObsArray} from "@kta20-core/utils/obs-array";
import {FetchError} from "@kta20-core/errors/fetch-error";
import {IStorage} from "@kta20-core/interfaces/i-storage";
import {Store} from "@kta20-core/models/store";
import {EventTarget} from "@kta20-core/core-module";
import {NAME_SPACE} from "@kta20-core/constants";
import {NetworkService} from "@kta20-core/net/network-service";
import {CustomError} from "@kta20-core/errors/custom-error";


export class HttpService extends EventTarget {

    protected static inst: HttpService;
    protected getTokenBy: () => Promise<string>;
    protected wretch: WretchLib.Wretcher;
    private _networkService:NetworkService;
    protected _cacheStorage:IStorage;
    private _isRequestInProgress:boolean=false;

    static readonly Events = {
        REQUEST_IN_PROGRESS:`${NAME_SPACE}.${HttpService.name}.request-in-progress`,
        REQUEST_STOPPED:`${NAME_SPACE}.${HttpService.name}.request-stopped`,
    };

    //TODO: move to dedicated service
    set cacheStorage(storage:IStorage){
        this._cacheStorage = storage;
    }

    get cacheStorage():IStorage{
        return this._cacheStorage;
    }

    get networkService():NetworkService{
        return this._networkService;
    }

    get requestInProgress():boolean {
        return this._isRequestInProgress;
    }
    
    protected set isRequestInProgress(v:boolean){
        this._isRequestInProgress = v;
        this.dispatchEvent(new CustomEvent(v ? HttpService.Events.REQUEST_IN_PROGRESS : HttpService.Events.REQUEST_STOPPED));
    }

    //TODO: refactor this method to actually return <T>
    /**
     * All serviceApiUrl, getTokenBy, catcherAll are required the
     * first time the service get initialized, optional later.
     * @param serviceApiUrl
     * @param getTokenBy
     * @param cathers
     * @param options
     */
    static get<T extends typeof HttpService>(
        this: T,
        serviceApiUrl?: string,
        getTokenBy?: () => Promise<string>,
        cathers?: Array<{code:string,catcher:(err,wrecher)=>void}>,
        options:WretchLib.WretcherOptions = { mode: "cors", redirect: "follow"}
    ):HttpService {
        if (!this.inst) {
            if (!serviceApiUrl || !getTokenBy) throw new Error('Missing required params: "serviceApiUrl" or "getTokenBy" not defined.');

            this.inst = new this() as InstanceType<T>;
            this.inst.getTokenBy = getTokenBy;

            this.inst.wretch = WretchLib.default(serviceApiUrl).options(options);

            this.inst._networkService = NetworkService.get();

            if (cathers) cathers.forEach(ct => this.inst.wretch.catcher(ct.code, ct.catcher));
        }

        return this.inst;
    }

    //TODO: add cache storage encryption based on user auth token
    // so that if webapp is left open without logout (that clears)
    // the cache is not readable by someone else (this requires the
    // token to be kept in memory, not in localstorage as KT1 does now!)
    /**
     * @param entities
     * @param resourceUrl
     * @param cacheKey: avoid if you don't want to cache results
     * @throws {CustomError | FetchError}
     */
    async load<T>(entities:ObsArray<T>, resourceUrl:string, cacheKey?:string){

        try{
            if(cacheKey && !this._cacheStorage)
                throw new Error('You must set the `cacheStorage` to then load using a cache.');

            let cacheStore:Store<T>;

            if(cacheKey){
                cacheStore = Object.assign(new Store<T>(), (await this._cacheStorage.getItem(cacheKey,true)));
                if(cacheStore.data && cacheStore.data.length>0){
                    entities.clearSilent();
                    cacheStore.data.forEach(item => item=Object.assign(({} as T), item));
                    entities.push(...cacheStore.data);
                }
            }

            if(this.networkService.hasNetwork()){
                let data:any;

                try {
                    this.isRequestInProgress = true;

                    data = await this.wretch
                        .auth('Bearer '+await this.getTokenBy())
                        .url(resourceUrl)
                        .get()
                        .json();
                }
                catch (e) {
                    console.warn(e);
                    this.isRequestInProgress = false;
                    throw new FetchError();
                }

                let remoteStore:Store<T> = Object.assign(new Store<T>(), {
                    data:Array.isArray(data) ? data : [data] //always storing as array since we only have ObsArray to use
                });

                if(cacheKey && (remoteStore.version>cacheStore.version || cacheStore.isNewInstance())){
                    try{
                        await this._cacheStorage.setItem(cacheKey, remoteStore);
                    }
                    catch (e) {
                        //in case of disk usage limit or any other write error...
                        console.error(e);
                    }
                }

                entities.clearSilent().push(...remoteStore.data);

                this.isRequestInProgress = false;
            }
        }
        catch (genericError){
            console.error(genericError);
            throw CustomError.ensureTyped(genericError);
        }

    }

}